// Páginas legales (Términos / Aviso Legal / Privacidad) servidas en /legal/:doc.
//
// El contenido se CARGA DEL BACKEND (`GET /public/legal/{slug}`), que sirve la
// VERSIÓN VIGENTE (publicada) de cada documento. Así los textos se versionan en
// el tiempo sin redeploy del frontend. Si el backend no está disponible, se
// usa un contenido de respaldo mínimo para que la página nunca quede en blanco.
//
// SEO: título por documento; indexable (los buscadores valoran tener legales).

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from '../components.js';
import { useSeo } from '../seo.js';
import { fetchLegalDocument, type PublicLegalDocument } from '../publicApi.js';

interface FallbackDoc {
  slug: string;
  title: string;
  description: string;
  sections: { h: string; p: string }[];
}

export function LegalView() {
  const { t, i18n } = useTranslation();
  const { doc } = useParams<{ doc: string }>();

  /** Formatea una fecha ISO a un texto legible (o cadena vacía). */
  const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Slugs válidos y su metadata de respaldo (si el backend no responde). Los
  // títulos/descripciones vienen localizados; la sección de carga es genérica.
  const loadingSection = {
    h: t('marketing.legal.loadingSectionH'),
    p: t('marketing.legal.loadingSectionP'),
  };
  const FALLBACK: Record<string, FallbackDoc> = {
    terminos: {
      slug: 'terminos',
      title: t('marketing.legal.terminosTitle'),
      description: t('marketing.legal.terminosDescription'),
      sections: [loadingSection],
    },
    'aviso-legal': {
      slug: 'aviso-legal',
      title: t('marketing.legal.avisoTitle'),
      description: t('marketing.legal.avisoDescription'),
      sections: [loadingSection],
    },
    privacidad: {
      slug: 'privacidad',
      title: t('marketing.legal.privacidadTitle'),
      description: t('marketing.legal.privacidadDescription'),
      sections: [loadingSection],
    },
    subprocesadores: {
      slug: 'subprocesadores',
      title: t('marketing.legal.subprocesadoresTitle'),
      description: t('marketing.legal.subprocesadoresDescription'),
      sections: [loadingSection],
    },
    dpa: {
      slug: 'dpa',
      title: t('marketing.legal.dpaTitle'),
      description: t('marketing.legal.dpaDescription'),
      sections: [loadingSection],
    },
  };
  const fallback = doc ? FALLBACK[doc] : undefined;

  const [remote, setRemote] = useState<PublicLegalDocument | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!doc || !FALLBACK[doc]) return;
    const ctrl = new AbortController();
    setLoading(true);
    fetchLegalDocument(doc, ctrl.signal)
      .then((d) => setRemote(d))
      .catch(() => setRemote(null))
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [doc]);

  const title = remote?.title ?? fallback?.title ?? t('marketing.legal.fallbackTitle');
  const description = remote?.summary ?? fallback?.description ?? t('marketing.legal.fallbackDescription');
  const sections = useMemo(
    () => (remote?.sections?.length ? remote.sections : (fallback?.sections ?? [])),
    [remote, fallback],
  );

  useSeo({
    title: `${title} — saands`,
    description,
    path: doc ? `/legal/${doc}` : '/legal',
  });

  if (!doc || !fallback) return <Navigate to="/" replace />;

  // Marca de versión/actualización: usa los datos vigentes del backend si están.
  const updated = formatDate(remote?.effective_at ?? remote?.published_at);
  const eyebrow = remote
    ? updated
      ? t('marketing.legal.versionUpdated', { version: remote.version, date: updated })
      : t('marketing.legal.version', { version: remote.version })
    : loading
      ? t('marketing.legal.loadingVersion')
      : t('marketing.legal.versionUnavailable');

  // Idioma REAL del contenido servido (el backend fija `locale` por versión, hoy
  // 'es'). Se declara en el contenedor del documento para que los lectores de
  // pantalla lo pronuncien correctamente aunque la UI esté en otro idioma (WCAG
  // 2.2 AA — 3.1.2 Idioma de las partes). El fallback usa el idioma de la UI.
  const contentLang = remote?.locale?.trim() || i18n.language;

  return (
    <Container className="py-space-xl lg:py-16 max-w-3xl">
      {/* Jerarquía de encabezados: h1 (título del documento) → h2 (secciones),
          sin saltos de nivel. `lang` acota el idioma del contenido legal. */}
      <article lang={contentLang} className="flex flex-col gap-space-lg">
        <header className="flex flex-col gap-space-md items-start text-left max-w-3xl">
          {eyebrow && (
            <span className="font-label-caps text-label-caps text-primary uppercase tracking-widest">
              {eyebrow}
            </span>
          )}
          <h1 className="font-headline-lg text-headline-lg-mobile lg:text-headline-lg text-on-surface font-bold tracking-tight text-balance">
            {title}
          </h1>
          {description && (
            <p className="font-body-md text-body-md lg:text-body-lg text-on-surface-variant leading-relaxed">
              {description}
            </p>
          )}
        </header>
        <div className="flex flex-col gap-space-lg mt-space-md">
          {sections.map((s, i) => (
            <section key={`${s.h}-${i}`} className="flex flex-col gap-space-xs">
              <h2 className="font-headline-sm text-headline-md text-on-surface font-bold">{s.h}</h2>
              <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed whitespace-pre-line">
                {s.p}
              </p>
            </section>
          ))}
        </div>
        {remote && (
          <p className="font-badge-micro text-badge-micro text-outline uppercase tracking-wider mt-space-md">
            {t('marketing.legal.currentDoc', { version: remote.version, ref: remote.content_hash.slice(0, 12) })}
          </p>
        )}
      </article>
    </Container>
  );
}
